import { getPaymentLineItems, isGiftPurchaseItem, isStoreCreditPurchaseItem, isStripeEnabled } from "common";
import {
    LineItemType,
    PaymentMethod,
    PaymentMethodCopy,
    Purchase,
    PurchaseItem,
    Seller,
    SellerPaymentMethod,
} from "types";
import { isStripeConnectOAuth } from "./Seller";

const baseBackOfficeMethods = [
    PaymentMethod.CREDIT_CARD,
    PaymentMethod.CASH,
    PaymentMethod.CHECK,
    PaymentMethod.INVOICE,
    PaymentMethod.OTHER,
];

const bnplMethods = [PaymentMethod.KLARNA, PaymentMethod.AFFIRM, PaymentMethod.AFTERPAY, PaymentMethod.ZIP];

const isBnplAvailable = (seller: Seller) => isStripeEnabled(seller) && !isStripeConnectOAuth(seller.defaultGateway);

export interface PaymentMethodInfo {
    id: string | PaymentMethod;
    name: string;
}

export const getDefaultPaymentMethods = (seller: Seller): PaymentMethodInfo[] => {
    return Object.values(PaymentMethod).map((method) => ({
        id: method,
        name: getPaymentMethodName(seller, method)!,
    }));
};

export const getBackofficePaymentMethods = (seller: Seller, isRefund?: boolean): SellerPaymentMethod[] => {
    const comment = { enabled: true, title: "Comment", required: false };
    const sellerMethods = baseBackOfficeMethods
        .map((method) => {
            return method === PaymentMethod.OTHER ? null : { id: method, name: PaymentMethodCopy[method], comment };
        })
        .filter(Boolean) as SellerPaymentMethod[];

    if (isRefund && seller.preferences?.gift?.enabled) {
        sellerMethods.splice(3, 0, {
            id: PaymentMethod.STORE_CREDIT,
            name: PaymentMethodCopy[PaymentMethod.STORE_CREDIT],
            comment,
        });
    }

    // The other payment method is a catch-all method and should come very last
    const other = { id: PaymentMethod.OTHER, name: PaymentMethodCopy[PaymentMethod.OTHER], comment };
    sellerMethods.push(...(seller.paymentMethods?.filter((method) => !method?.deletedAt) ?? []), other);

    return sellerMethods;
};

/**
 * This is for returning methods that are not used in creating bookings, but may be used in creating transactions
 */
export const getAllSellerPaymentMethods = (seller: Seller): SellerPaymentMethod[] => {
    const methods = getBackofficePaymentMethods(seller);

    const additionalMethods = [
        PaymentMethod.GIFT,
        PaymentMethod.ACH,
        PaymentMethod.VOUCHER,
        PaymentMethod.STORE_CREDIT,
        PaymentMethod.INTENT,
        ...(isBnplAvailable(seller) ? bnplMethods : []),
    ];
    for (const method of additionalMethods) {
        methods.push({ id: method, name: PaymentMethodCopy[method] });
    }

    return methods;
};

export const isCommentRequired = (seller: Seller, paymentMethod: ID) => {
    const methods = seller.paymentMethods ?? [];
    const method = methods.find((pm) => pm.id === paymentMethod);
    return (method?.comment?.enabled && method?.comment?.required) ?? false;
};

export const isCommentAllowed = (seller: Seller, paymentMethod: ID) => {
    const methods = seller.paymentMethods ?? [];
    const method = methods.find((pm) => pm.id === paymentMethod);
    return (method?.comment?.enabled && method?.comment?.enabled) ?? false;
};

export const getPaymentMethodName = (seller: Seller, method: PaymentMethod | ID) => {
    const realMethod: PaymentMethod = method as PaymentMethod; // For preventing lint errors

    if (PaymentMethodCopy[realMethod]) {
        return PaymentMethodCopy[realMethod];
    }

    return seller.paymentMethods?.find(({ id }) => id === method)?.name;
};

export const getCreditCardPaymentMethod = () => {
    const comment = { enabled: true, title: "Comment", required: false };

    return [{ id: PaymentMethod.CREDIT_CARD, name: PaymentMethodCopy[PaymentMethod.CREDIT_CARD], comment }];
};

interface getAllowedPaymentMethodsProps {
    seller: Seller;
    isRefund: boolean;
    isInvoiceAllowed: boolean;
    excludePaymentMethods?: Array<PaymentMethod | ID>;
}

export const getAllowedPaymentMethods = ({
    seller,
    isRefund,
    isInvoiceAllowed,
    excludePaymentMethods,
}: getAllowedPaymentMethodsProps) => {
    return getBackofficePaymentMethods(seller, isRefund).filter((method) => {
        const isMethodExcluded = excludePaymentMethods?.includes(method.id);
        const isInvoice = method.id === PaymentMethod.INVOICE;
        const shouldIncludeInvoice = isInvoiceAllowed && !isRefund;

        if (isInvoice) {
            return !isMethodExcluded && shouldIncludeInvoice;
        }

        return !isMethodExcluded;
    });
};

export const getExcludedPaymentMethods = (purchase: Purchase, item?: PurchaseItem) => {
    const excludedPaymentMethods = [];
    if (isStoreCreditPurchaseItem(item) || isGiftPurchaseItem(item) || !purchase.organizer) {
        excludedPaymentMethods.push(PaymentMethod.STORE_CREDIT);
    }

    const hasCreditCardPayment = getPaymentLineItems(purchase).some((lineItem) => {
        return lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === PaymentMethod.CREDIT_CARD;
    });

    if (!hasCreditCardPayment) {
        excludedPaymentMethods.push(PaymentMethod.CREDIT_CARD);
    }

    return excludedPaymentMethods;
};

const ONLINE_PAYMENT_METHODS = new Set([PaymentMethod.CREDIT_CARD, ...bnplMethods]);

export const getOnlinePaymentMethodsFromPurchase = (purchase: Purchase): SellerPaymentMethod[] => {
    const paymentLineItems = getPaymentLineItems(purchase);
    const onlinePaymentMethods: SellerPaymentMethod[] = [];

    for (const lineItem of paymentLineItems) {
        const method = lineItem.payment?.method;
        if (method && ONLINE_PAYMENT_METHODS.has(method as PaymentMethod)) {
            const existingMethod = onlinePaymentMethods.find((m) => m.id === method);
            if (!existingMethod) {
                onlinePaymentMethods.push({
                    id: method,
                    name: PaymentMethodCopy[method as PaymentMethod] || method,
                    comment: { enabled: true, title: "Comment", required: false },
                });
            }
        }
    }

    return onlinePaymentMethods;
};
